import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { RiskDataService } from "@app/risk/services/RiskDataService";
import { Pages } from "@constants/pages/Pages";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { BaseHighchartWidget } from "../BaseHighchartWidget";
import { ComparisonType } from "@enums/workspace/ComparisonType";
import { WidgetFilterKey } from "@app/dashboard/shared/models/WidgetFilterKey";
import { RiskWorkspaceModel } from "@app/risk/models/RiskWorkspaceModel";
import { nameof } from "@methods/CommonMethods";
import { firstValueFrom } from "rxjs";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "risks-per-catalogue-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    providers: [provideTranslocoScope("dashboard")]
})
export class RisksPerCatalogueWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;

    private ds = inject(RiskDataService);
    private pages = inject(Pages);

    constructor() {
        super();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.Organization] =
            nameof<RiskWorkspaceModel>("organizationID").toString();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.BusinessDimension] =
            nameof<RiskWorkspaceModel>("businessDimensionIDs").toString();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.FrameworkDimension] =
            nameof<RiskWorkspaceModel>("frameworkDimensionIDs").toString();
    }

    protected async getChartConfig() {
        await this.preloadDashboardScope();

        const filters = this.getFilters();
        const data = await firstValueFrom(this.ds.getRisksPerCatalogueWidgetInfo(filters));

        this.config.noData = !data || data.length === 0 || !data.some((d) => d.y > 0);
        if (this.config.noData) {
            return null;
        }

        const config = this.getBaseChartConfig(null, [
            <any>{
                name: this.transloco.translate("dashboard.risksPerCatalogueWidget.seriesName"),
                data,
            },
        ]);

        config.tooltip = {
            headerFormat: "<b>{point.key}:</b> {point.y}",
            pointFormat: "",
        };

        config.xAxis = {
            type: "category",
        };

        config.yAxis = {
            min: 0,
            allowDecimals: false,
            title: {
                text: this.transloco.translate("dashboard.risksPerCatalogueWidget.yAxisTitle"),
            },
        };

        return config;
    }

    protected async openWorkspace(ev) {
        const searchKeywords: SearchKeyword[] = await this.getSearchKeywords(ev.point.name);
        const preset = await this.getBasePreset(searchKeywords);
        this.openPreset(this.pages.RiskOverview, preset);
    }

    private async getSearchKeywords(catalogue: string) {
        const searchKeywords: SearchKeyword[] = [
            this.getSearchKeyword(
                nameof<RiskWorkspaceModel>("riskCatalogue"),
                catalogue,
                ComparisonType.Contains
            ),
        ];

        return searchKeywords;
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.RiskOverview, preset);
    }
}
